import React from 'react';
import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { getDoc, Timestamp, doc, GeoPoint } from "firebase/firestore"
import { Box, FormControlLabel, IconButton, InputAdornment, MenuItem, Switch, TextField, FormLabel, Checkbox, Button, CircularProgress, Tooltip } from "@mui/material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from '../../../Config/firebase';
import { toast } from "react-toastify";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import CloseIcon from "@mui/icons-material/Close";
import { db } from '../../../Config/firebase.ts';
import { toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { SubscriptionType } from '../../../enums.ts';
import { selectBankAccounts, selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { getGeoHash, getLatLong } from '../utilityFunctions';

const GEO_LOC_API = import.meta.env.VITE_GEOLOCATION_API_KEY || process.env.VITE_GEOLOCATION_API_KEY;

interface MultiMonthPrices {
    FlexibleMonthly?: string;
    Quarterly?: string;
    Semester?: string;
    Annual?: string;
}

interface MultiMonthPauseDays {
    FlexibleMonthly?: string;
    Quarterly?: string;
    Semester?: string;
    Annual?: string;
}

interface CountPricing {
    count: string;
    price: string;
    validity: string;
}

interface CountPricingError {
    count: boolean;
    price: boolean;
    validity: boolean;
}

interface PlanDetails {
    name: string;
    price: string;
    validity: string;
    isCustom: boolean;
}

interface PlanErrors {
    name: boolean;
    price: boolean;
    validity: boolean;
}

type Binary = 0 | 1;

interface FormDataType {
    geoLocation: {
        hash: string;
        latlang: GeoPoint;
    };
    placeName: string;
    placeAddress: string;
    eName: string;
    eSummary: string;
    eSubCategory: string;
    isOpen: boolean;
    eCategory: string;
    eStartTime: Date;
    eEndTime: Date;
    paymentProfileId: number;
    eTermsConditions: string;
    eRegsStartTime: Date;
}

export default function EditSubscriptionModal() {

    // ------------------------DialogHandlers

    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.EditSubscriptionModalOpen);

    const handleClose = () => {
        resetState();
        Dispatch(closeModal("EditSubscriptionModalOpen"))
    };


    // ------------------------Form States

    const [Name, setName] = React.useState<string>("")
    const [Price, setPrice] = React.useState<string>("")
    const [, setIsError] = React.useState<boolean>(false)
    const [paymentProfileID, setPaymentProfileID] = useState<number>(0)
    const [showMeInList, setShowMeInList] = React.useState<boolean>(true)
    const [SubscriptionPaymentType, setSubscriptionPaymentType] = React.useState<string>("Monthly")
    const [multiMonthPrices, setMultiMonthPrices] = React.useState<MultiMonthPrices>({
        FlexibleMonthly: "",
        Quarterly: "",
        Semester: "",
        Annual: "",
    })
    const [isCountHourly, setIsCountHourly] = React.useState<boolean>(false)
    const [groupLink, setGroupLink] = React.useState<string>("");
    const [advance, setAdvance] = React.useState<string>("")
    const [multiMonthPauseDays, setMultiMonthPauseDays] = React.useState<MultiMonthPauseDays>({
        FlexibleMonthly: "",
        Quarterly: "",
        Semester: "",
        Annual: "",

    });
    const [NoGSTBatch, setNoGSTBatch] = useState<boolean>(false);
    const [markupAllowed, setMarkupAllowed] = useState<boolean>(false);
    const [showWorkingDays, setShowWorkingDays] = useState(false);
    const [workingDays, setWorkingDays] = useState<[Binary, Binary, Binary, Binary, Binary, Binary, Binary]>([0, 0, 0, 0, 0, 0, 0]);
    const [placeName, setPlaceName] = useState<string>("");

    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const handleDaySelection = (index: number) => {
        setWorkingDays(prev => {
            const newDays = [...prev] as [Binary, Binary, Binary, Binary, Binary, Binary, Binary];
            newDays[index] = prev[index] === 1 ? 0 : 1;
            return newDays;
        });
    };

    // ------------------------State Setters

    const ArbForEditSubscriptionModal = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditSubscriptionModal)
    const subscriptionId = ArbForEditSubscriptionModal.id
    const ServiceProviderID = useAppSelector(selectEffectiveUID)

    // Add new state for custom subscription image
    const [customSubscriptionImage, setCustomSubscriptionImage] = useState<{
        file: File | null;
        preview: string | null;
    }>({ file: null, preview: null });

    // Add new states
    const [countPricings, setCountPricings] = useState<CountPricing[]>([{
        count: '',
        price: '',
        validity: '365'
    }]);

    const [countPricingErrors, setCountPricingErrors] = useState<CountPricingError[]>([{
        count: false,
        price: false,
        validity: false
    }]);

    // Add new states for multi-month plans
    const [multiMonthPlans, setMultiMonthPlans] = useState<PlanDetails[]>([]);
    const [showPlanSelector, setShowPlanSelector] = useState(false);
    const [selectedPlanType, setSelectedPlanType] = useState<string>('');
    const [planErrors, setPlanErrors] = useState<PlanErrors[]>([]);

    const predefinedPlans = [
        { name: 'Flexible Monthly', value: 'FlexibleMonthly' },
        { name: 'Quarterly', value: 'Quarterly' },
        { name: 'Semester', value: 'Semester' },
        { name: 'Custom Plan', value: 'Custom' }
    ];

    const handleAddPlan = () => {
        setShowPlanSelector(true);
    };

    const handlePlanTypeSelect = (planType: string) => {
        if (planType !== 'Custom' && multiMonthPlans.some(plan => !plan.isCustom && predefinedPlans.find(p => p.name === plan.name)?.value === planType)) {
            toast.error('This plan type is already added');
            return;
        }

        if (planType === 'Custom') {
            setMultiMonthPlans([...multiMonthPlans, {
                name: `Custom Plan ${multiMonthPlans.length + 1}`,
                price: '',
                validity: '',
                isCustom: true
            }]);
        } else {
            const planName = predefinedPlans.find(p => p.value === planType)?.name || planType;
            setMultiMonthPlans([...multiMonthPlans, {
                name: planName,
                price: '',
                validity: planType === 'Quarterly' ? '90' : planType === 'Semester' ? '180' : planType === 'Annual' ? '365' : '30',
                isCustom: false
            }]);
        }
        setShowPlanSelector(false);
    };

    const handlePlanChange = (index: number, field: keyof PlanDetails, value: string) => {
        const updatedPlans = [...multiMonthPlans];
        updatedPlans[index] = { ...updatedPlans[index], [field]: value };
        setMultiMonthPlans(updatedPlans);
    };

    const handleRemovePlan = (index: number) => {
        setMultiMonthPlans(multiMonthPlans.filter((_, i) => i !== index));
    };

    // Add handler for subscription image change
    const handleSubscriptionImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check file size (5MB limit)
        if (file.size > 8 * 1024 * 1024) {
            toast.error('Image size should be less than 8MB');
            return;
        }


        const reader = new FileReader();
        reader.onloadend = () => {
            setCustomSubscriptionImage({
                file,
                preview: reader.result as string
            });
        };
        reader.readAsDataURL(file);
    };

    // Add function to upload subscription image
    const handleSubscriptionImageUpload = async (): Promise<string | null> => {
        if (!customSubscriptionImage.file) return null;

        try {
            const timestamp = Date.now();
            const storageRef = ref(storage, `subscriptionImages/${ServiceProviderID}/${timestamp}_${customSubscriptionImage.file.name}`);
            await uploadBytes(storageRef, customSubscriptionImage.file);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error("Error uploading subscription image:", error);
            toast.error('Failed to upload subscription image');
            return null;
        }
    };

    // Add handlers for count pricing
    const handleCountPricingChange = (index: number, field: keyof CountPricing, value: string) => {
        if (field === 'price' || field === 'count' || field === 'validity') {
            const regex = /^\d*\.?\d*$/;
            if (regex.test(value) || value === '') {
                const newPricings = [...countPricings];
                newPricings[index][field] = value;
                setCountPricings(newPricings);
            }
        } else {
            const newPricings = [...countPricings];
            newPricings[index] = { ...newPricings[index], [field]: value };
            setCountPricings(newPricings);
        }
    };

    const handleAddCountPricing = () => {
        setCountPricings([...countPricings, {
            count: '',
            price: '',
            validity: '365'
        }]);
        setCountPricingErrors([...countPricingErrors, {
            count: false,
            price: false,
            validity: false
        }]);
    };

    const handleRemoveCountPricing = (index: number) => {
        if (countPricings.length > 1) {
            setCountPricings(countPricings.filter((_, i) => i !== index));
            setCountPricingErrors(countPricingErrors.filter((_, i) => i !== index));
        }
    };

    // Add new state for location
    const [location, setLocation] = useState<any>(null);

    // Add location effect
    useEffect(() => {
        if (location && location.value.place_id) {
            // Only call getLatLong if we have a place_id (new location selection)
            getLatLong(location.value.place_id).then((geometry: { lat: any, lng: any }) => {
                const newPlaceName = location.value.structured_formatting.main_text;
                const geoPoint = new GeoPoint(geometry.lat, geometry.lng);
                setFormData((prev: FormDataType) => ({
                    ...prev,
                    geoLocation: {
                        hash: geoPoint != null ? getGeoHash(geoPoint) || "" : "",
                        latlang: geoPoint
                    },
                    placeName: newPlaceName,
                    placeAddress: location.label,
                    eName: prev.eName,
                    eSummary: prev.eSummary,
                    eSubCategory: prev.eSubCategory,
                    isOpen: prev.isOpen,
                    eCategory: prev.eCategory,
                    eStartTime: prev.eStartTime,
                    eEndTime: prev.eEndTime,
                    paymentProfileId: prev.paymentProfileId,
                    eTermsConditions: prev.eTermsConditions,
                    eRegsStartTime: prev.eRegsStartTime
                }));
                // Also update the placeName state for the input field
                setPlaceName(newPlaceName);
            });
        }
    }, [location]);

    // Modify useEffect for fetching subscription data
    useEffect(() => {
        const fetchSubscriptionData = async () => {
            try {
                const subscriptionDocPath = `/ServiceProviders/${ServiceProviderID}/Subscriptions/${subscriptionId}`;
                const subscriptionDocRef = doc(db, subscriptionDocPath);
                const subscriptionDoc = await getDoc(subscriptionDocRef);
                if (subscriptionDoc.exists()) {
                    const data = subscriptionDoc.data();

                    setName(data.Name);
                    setPrice(data.Price?.toString() || "");
                    setPaymentProfileID(data.paymentProfileId);
                    setShowMeInList(data.showMeInList);
                    setGroupLink(data.groupLink || "");
                    setSubscriptionPaymentType(data.subscriptionType);
                    setIsCountHourly(data.isCountHourly || false);
                    setNoGSTBatch(data.NoGSTBatch || false);
                    setMarkupAllowed(data.markupAllowed || false);
                    // Handle cyclePriceArray if it exists
                    if (data.subscriptionType === "MultiMonth" && data.cyclePriceArray && data.cyclePriceArray.length > 0) {
                        const plans: PlanDetails[] = data.cyclePriceArray.map((cycle: any) => {
                            const predefinedPlan = predefinedPlans.find(p => p.value === cycle.billingCycle);
                            return {
                                name: predefinedPlan ? predefinedPlan.name : cycle.billingCycle,
                                price: cycle.price.toString(),
                                validity: cycle.validity.toString(),
                                isCustom: !predefinedPlan
                            };
                        });
                        setMultiMonthPlans(plans);
                    } else if (data.subscriptionType === "Count" && data.cyclePriceArray) {
                        const countPricingData = data.cyclePriceArray.map((item: any) => ({
                            count: item.count.toString(),
                            price: item.price.toString(),
                            validity: item.validity.toString()
                        }));
                        setCountPricings(countPricingData);
                    }

                    setAdvance(data.advance?.toString() || "");

                    // Set working days if they exist
                    if (data.days) {
                        setWorkingDays(data.days);
                    } else {
                        setWorkingDays([1, 1, 1, 1, 1, 1, 1]);
                    }

                    // Set custom image if it exists
                    if (data.picture) {
                        setCustomSubscriptionImage({
                            file: null,
                            preview: data.picture
                        });
                    }

                    // Set location data if it exists
                    if (data.geoLocation) {
                        setLocation({
                            label: data.placeAddress,
                            value: {
                                structured_formatting: {
                                    main_text: data.placeName
                                }
                            }
                        });

                        // Also directly set the formData for existing location
                        setFormData((prev: FormDataType) => ({
                            ...prev,
                            geoLocation: {
                                hash: data.geoLocation.hash || "",
                                latlang: data.geoLocation.latlang || new GeoPoint(0, 0)
                            },
                            placeName: data.placeName || "",
                            placeAddress: data.placeAddress || ""
                        }));

                        // Set placeName state for the input field
                        setPlaceName(data.placeName || "");
                    }
                }
            } catch (error) {
                console.error("Error fetching subscription data:", error);
            }
        };
        if (ServiceProviderID && subscriptionId) {
            fetchSubscriptionData();
        }
    }, [subscriptionId, open]);


    // ----------------------- Reset States
    const resetState = () => {
        setName("");
        setPrice("");
        setIsError(false);
        setPaymentProfileID(0);
        setModalError({
            Name: false,
            Price: false,
            MultiMonthPrices: {
                nullFilled: false,
                Quarterly: false,
                Semester: false,
                Annual: false,
                FlexibleMonthly: false,
            }
        })
        setGroupLink("");
        setAdvance("");
        setShowWorkingDays(false);
        setWorkingDays([0, 0, 0, 0, 0, 0, 0]);
        setCustomSubscriptionImage({ file: null, preview: null });
        setCountPricings([{
            count: '',
            price: '',
            validity: '365'
        }]);
        setCountPricingErrors([{
            count: false,
            price: false,
            validity: false
        }]);
        setMultiMonthPlans([]);
        setShowPlanSelector(false);
        setSelectedPlanType('');
        setPlanErrors([]);
        setLocation(null);
        setNoGSTBatch(false);
        setMarkupAllowed(false);
        setPlaceName("");
    };


    // ------------------------Handle Change Form States


    const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value)
    }


    const handleChangePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs

        if (!value) {
            setPrice("")
            return;
        }

        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {

            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }

        setPrice(value)
    }


    const handleChangePaymentProfile = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPaymentProfileID(Number(event.target.value))
    }

    const handleChangeShowMeInList = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowMeInList(event.target.checked)
    }

    const handleChangeSubscriptionPaymentType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubscriptionPaymentType(event.target.value)
    }

    const handleMultiMonthPriceChange = (period: keyof MultiMonthPrices) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value || /^\d*\.?\d*$/.test(value)) {
            setMultiMonthPrices(prev => ({
                ...prev,
                [period]: value
            }));
        }
    };

    const handleGroupLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupLink(event.target.value);
    };

    const handleChangeAdvance = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value || /^\d*\.?\d*$/.test(value)) {
            setAdvance(value);
        }
    };

    const handlePauseDaysChange = (period: keyof MultiMonthPauseDays) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // Only allow numbers
        if (!value || /^\d*$/.test(value)) {
            setMultiMonthPauseDays(prev => ({
                ...prev,
                [period]: value
            }));
        }
    };

    const handlePlaceNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPlaceName = event.target.value;
        setPlaceName(newPlaceName);
        // Also update formData
        setFormData(prev => ({
            ...prev,
            placeName: newPlaceName
        }));
    };


    // ------------------------Error Handlers

    const [modalError, setModalError] = React.useState({
        Name: false,
        Price: false,
        MultiMonthPrices: {
            nullFilled: false,
            Quarterly: false,
            Semester: false,
            Annual: false,
            FlexibleMonthly: false,
        }
    });

    function CheckErrors(): boolean {
        setIsError(false)
        let localerror: boolean = false

        if (Name.trim() === "") {
            setIsError(true)
            localerror = true
            setModalError((prev) => {
                return { ...prev, Name: true }
            })
        } else {
            setModalError((prev) => {
                return { ...prev, Name: false }
            })
        }

        if (SubscriptionPaymentType === "MultiMonth") {
            if (multiMonthPlans.length === 0) {
                localerror = true;
                setIsError(true);
                setModalError((prev) => ({
                    ...prev,
                    MultiMonthPrices: { ...prev.MultiMonthPrices, nullFilled: true }
                }));
            } else {
                // Check each plan for errors
                const newPlanErrors = multiMonthPlans.map(plan => ({
                    name: plan.isCustom && !plan.name.trim(),
                    price: !plan.price || Number(plan.price) < 6,
                    validity: !plan.validity || Number(plan.validity) <= 0
                }));

                // If any plan has errors, set the error flag
                if (newPlanErrors.some(error => error.name || error.price || error.validity)) {
                    localerror = true;
                    setIsError(true);
                }

                setPlanErrors(newPlanErrors);
            }
        } else if (SubscriptionPaymentType === "Monthly") {
            if (!Price || Number(Price) < 6) {
                localerror = true
                setIsError(true)
                setModalError((prev) => {
                    return { ...prev, Price: true }
                })
            } else {
                setModalError((prev) => {
                    return { ...prev, Price: false }
                })
            }
        } else if (SubscriptionPaymentType === "Count") {
            const newErrors = [...countPricingErrors];
            countPricings.forEach((pricing, index) => {
                newErrors[index] = {
                    count: !pricing.count || Number(pricing.count) <= 0,
                    price: !pricing.price || Number(pricing.price) < 6,
                    validity: !pricing.validity || Number(pricing.validity) <= 0
                };

                if (newErrors[index].count || newErrors[index].price || newErrors[index].validity) {
                    localerror = true;
                }
            });
            setCountPricingErrors(newErrors);
        }

        return localerror
    }

    // console.log((multiMonthPauseDays.Quarterly && !multiMonthPrices.Quarterly),"pause days and price")
    // --------------------------Form Options
    //--------------------Listening for bank accounts for options
    const BankAccounts = useAppSelector(selectBankAccounts);
    const bankAccountOptions = BankAccounts?.map(bankAccount => {
        return (

            bankAccount.iFSC.slice(0, 4) + " - " + bankAccount.accountNo
        )
    }) || []

    // --------------------------On Submit Operation

    const { update } = useBackend(`/ServiceProviders/${ServiceProviderID}/Subscriptions`)

    // Add loading state from ModalSlice
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);

    // Modify onSubmit function to handle loading state
    async function onSubmit() {
        Dispatch(toggleLoading(true)); // Start loading
        try {
            const Localerrors = CheckErrors()

            if (Localerrors) {
                Dispatch(toggleLoading(false));
                return;
            }

            // Upload new image if changed
            let imageUrl = customSubscriptionImage.preview;
            if (customSubscriptionImage.file) {
                imageUrl = await handleSubscriptionImageUpload();
            }

            const reqDoc = {
                ModifiedAt: Timestamp.fromDate(new Date()),
                Name: Name,
                Price: SubscriptionPaymentType === "Monthly" ? Number(Price) : 0,
                cyclePriceArray: SubscriptionPaymentType === "MultiMonth"
                    ? multiMonthPlans.map(plan => ({
                        billingCycle: plan.isCustom ? plan.name : predefinedPlans.find(p => p.name === plan.name)?.value || plan.name,
                        price: Number(plan.price),
                        validity: Number(plan.validity),
                        pauseDays: 365
                    }))
                    : SubscriptionPaymentType === "Count"
                        ? countPricings.map(pricing => ({
                            billingCycle: isCountHourly ? SubscriptionType.Hours : SubscriptionType.Count,
                            count: Number(pricing.count),
                            price: Number(pricing.price),
                            validity: Number(pricing.validity)
                        }))
                        : [],
                paymentProfileId: paymentProfileID,
                accountId: BankAccounts && BankAccounts[paymentProfileID] ? BankAccounts[paymentProfileID].razorpayAccountId : null,
                showMeInList: showMeInList,
                picture: imageUrl,
                groupLink: groupLink,
                advance: advance ? Number(advance) : 0,
                days: workingDays,
                subscriptionType: SubscriptionPaymentType,
                // Add location data
                geoLocation: location ? {
                    hash: formData.geoLocation.hash,
                    latlang: formData.geoLocation.latlang,
                    placeName: formData.placeName,
                    address: formData.placeAddress
                } : null,
                placeName: formData.placeName,
                NoGSTBatch: NoGSTBatch,
                markupAllowed: markupAllowed,
                placeAddress: formData.placeAddress
            }

            handleClose();
            await update(ArbForEditSubscriptionModal.id, reqDoc);
            resetState();
            toast.success("Subscription updated successfully!");
        } catch (error) {
            console.error("Error updating subscription:", error);
            toast.error("Failed to update subscription");
        } finally {
            Dispatch(toggleLoading(false)); // End loading
        }
    }

    const [formData, setFormData] = useState<FormDataType>({
        geoLocation: {
            hash: "",
            latlang: new GeoPoint(0, 0)
        },
        placeName: "",
        placeAddress: "",
        eName: "",
        eSummary: "",
        eSubCategory: "",
        isOpen: true,
        eCategory: "",
        eStartTime: new Date(),
        eEndTime: new Date(),
        paymentProfileId: 0,
        eTermsConditions: "",
        eRegsStartTime: new Date()
    });

    return (
        <React.Fragment>

            <Dialog
                sx={{
                    padding: 0,
                    '& .MuiPaper-root': {
                        borderRadius: '10px',
                    },
                }}
                maxWidth="md"
                open={open}
                PaperProps={{
                    component: 'div',

                }}
            >

                <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className={"bg-white"}>
                    <div className={"header pb-[20px]"}>
                        <p className={"text-cf_heading font-[600] "}>Edit Subscription</p>
                        <p className={"text-cf_small font-[400] "}>Change Price, bank account and control Visibility</p>
                    </div>
                    <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton></div>

                    <div className={"w-[550px] flex flex-col items-center mb-6"}>
                        <div className="relative w-[300px] group">
                            <img
                                className="aspect-[4/3] w-full rounded-lg shadow-lg object-cover"
                                src={customSubscriptionImage.preview || 'https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FSubscriptionImage.png?alt=media&token=b3a2a2a2-2b2a-4b2a-8b2a-2b2a2b2a2b2a'}
                                alt="Subscription"
                            />

                            {/* Overlay with upload button */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center">
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{
                                        opacity: 0,
                                        transform: 'translateY(10px)',
                                        transition: 'all 0.3s',
                                        '.group:hover &': {
                                            opacity: 1,
                                            transform: 'translateY(0)',
                                        },
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        color: '#1D4ED8',
                                        borderColor: '#1D4ED8',
                                        '&:hover': {
                                            backgroundColor: '#1D4ED8',
                                            color: "#FFFFFF",
                                            borderColor: '#1D4ED8',
                                        },
                                    }}
                                >
                                    <span className="flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Change Image
                                    </span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleSubscriptionImageChange}
                                    />
                                </Button>
                            </div>
                        </div>


                    </div>

                    <div className={"pt-0"}>
                        <div className={""}>
                            <div className={" w-full"}>

                                <Box
                                    component={"div"}
                                    sx={{}}
                                >
                                    <div className={"TextFields flex flex-col gap-4 w-[550px]"}>
                                        <TextField
                                            onChange={handleChangeName}
                                            value={Name}
                                            size={"small"}
                                            required={true}
                                            id="Name"
                                            label="Name"
                                            error={modalError.Name}
                                            helperText={modalError.Name ? "Name is required" : ""}
                                            variant="outlined"
                                            fullWidth={true}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'// Default border color
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                },
                                                fontWeight: 400,

                                            }}
                                        />


                                        {SubscriptionPaymentType === "MultiMonth" ? (
                                            <div className="space-y-4">
                                                <p className="text-sm font-semibold">Edit subscription plans <span className="text-xs font-semibold">( Will be only applied to new students)</span> </p>

                                                <div className="space-y-3">
                                                    {multiMonthPlans.map((plan, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50 hover:shadow-md transition-shadow"
                                                        >
                                                            <div className="flex-1 flex items-center gap-3 min-w-0">
                                                                {plan.isCustom ? (
                                                                    <TextField
                                                                        value={plan.name}
                                                                        size="small"
                                                                        label="Plan Name"
                                                                        onChange={(e) => handlePlanChange(index, 'name', e.target.value)}
                                                                        variant="outlined"
                                                                        error={planErrors[index]?.name}
                                                                        helperText={planErrors[index]?.name ? "Plan name required" : ""}
                                                                        sx={{
                                                                            minWidth: '180px',
                                                                            maxWidth: '180px',
                                                                            '& .MuiOutlinedInput-root': { borderColor: 'grey', borderRadius: '10px' },
                                                                            '& .MuiInputBase-input': { fontSize: '14px' },
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="min-w-[180px] max-w-[180px] flex items-center h-[40px] px-3 bg-white border rounded-lg">
                                                                        <span className="text-gray-700 font-medium truncate">{plan.name}</span>
                                                                    </div>
                                                                )}
                                                                <TextField
                                                                    value={plan.validity}
                                                                    size="small"
                                                                    label="Duration (days)"
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        if (!value || /^\d*$/.test(value)) { // Only allow numbers
                                                                            handlePlanChange(index, 'validity', value);
                                                                        }
                                                                    }}
                                                                    variant="outlined"
                                                                    error={planErrors[index]?.validity}
                                                                    helperText={planErrors[index]?.validity ? "Duration required" : ""}
                                                                    sx={{
                                                                        minWidth: '140px',
                                                                        maxWidth: '140px',
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                            '& .MuiInputBase-input': { fontSize: '14px' },
                                                                        }
                                                                    }}
                                                                />

                                                                <TextField
                                                                    value={plan.price}
                                                                    size="small"
                                                                    label="Price"
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        if (!value || /^\d*$/.test(value)) { // Only allow numbers
                                                                            handlePlanChange(index, 'price', e.target.value)
                                                                        }
                                                                    }}

                                                                    variant="outlined"
                                                                    error={planErrors[index]?.price}
                                                                    helperText={planErrors[index]?.price ? "Min ₹6 required" : ""}
                                                                    InputProps={{
                                                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                                    }}
                                                                    sx={{
                                                                        minWidth: '140px',
                                                                        maxWidth: '140px',
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                            '& .MuiInputBase-input': { fontSize: '14px' },
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="flex items-center pl-2">
                                                                <IconButton
                                                                    onClick={() => handleRemovePlan(index)}
                                                                    sx={{
                                                                        color: 'grey',
                                                                        '&:hover': { color: 'red' },
                                                                        padding: '8px'
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    ))}


                                                    <div className="p-4 border rounded-lg bg-white">
                                                        <p className="mb-2 text-sm font-medium">Add Plan Type:</p>
                                                        <div className="grid grid-cols-3 gap-2">
                                                            {predefinedPlans.map((plan) => {
                                                                const isDisabled = plan.value !== 'Custom' &&
                                                                    multiMonthPlans.some(p => !p.isCustom &&
                                                                        predefinedPlans.find(pp => pp.name === p.name)?.value === plan.value);

                                                                return (
                                                                    <Button
                                                                        key={plan.value}
                                                                        onClick={() => handlePlanTypeSelect(plan.value)}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={isDisabled}
                                                                        sx={{
                                                                            textTransform: 'none',
                                                                            borderRadius: '8px',
                                                                            color: isDisabled ? 'grey' : '#1D4ED8',
                                                                            borderColor: isDisabled ? 'grey' : '#1D4ED8',
                                                                            '&:hover': {
                                                                                backgroundColor: isDisabled ? 'transparent' : '#1D4ED8',
                                                                                color: isDisabled ? 'grey' : '#FFFFFF',
                                                                                borderColor: isDisabled ? 'grey' : '#1D4ED8',
                                                                            },
                                                                        }}
                                                                    >
                                                                        {plan.name}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                </div>

                                                {modalError.MultiMonthPrices.nullFilled &&
                                                    <p className="text-sm text-cc_error">Please add at least one plan</p>
                                                }
                                            </div>
                                        ) : SubscriptionPaymentType === "Count" ? (
                                            <div className="space-y-1 mb-2">
                                                <p className="text-sm font-semibold mb-4">Define pricing plans <span className="text-xs font-semibold">( Will be only applied to new students)</span> </p>
                                                {countPricings.map((pricing, index) => (
                                                    <div key={index} className="flex items-start gap-2">
                                                        <div className="flex flex-col">
                                                            <TextField
                                                                value={pricing.count}
                                                                size="small"
                                                                label="Number of Classes"
                                                                onChange={(e) => handleCountPricingChange(index, 'count', e.target.value)}
                                                                error={countPricingErrors[index]?.count}
                                                                helperText={countPricingErrors[index]?.count ? "Must be > 0" : " "}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                        '& .MuiInputBase-input': { fontSize: '14px' },
                                                                    },
                                                                    width: '150px'
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <TextField
                                                                value={pricing.price}
                                                                size="small"
                                                                label="Price"
                                                                onChange={(e) => handleCountPricingChange(index, 'price', e.target.value)}
                                                                error={countPricingErrors[index]?.price}
                                                                helperText={countPricingErrors[index]?.price ? "Min ₹6 required" : " "}
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                        '& .MuiInputBase-input': { fontSize: '14px' },
                                                                    },
                                                                    width: '150px'
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <TextField
                                                                value={pricing.validity}
                                                                size="small"
                                                                label="Valid Days"
                                                                onChange={(e) => handleCountPricingChange(index, 'validity', e.target.value)}
                                                                error={countPricingErrors[index]?.validity}
                                                                helperText={countPricingErrors[index]?.validity ? "Must be > 0" : " "}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': { borderColor: 'grey', borderRadius: '10px' },
                                                                        '& .MuiInputBase-input': { fontSize: '14px' },
                                                                    },
                                                                    width: '150px'
                                                                }}
                                                            />
                                                        </div>
                                                        <IconButton
                                                            onClick={() => handleRemoveCountPricing(index)}
                                                            disabled={countPricings.length === 1}
                                                            sx={{
                                                                color: 'grey',
                                                                '&:hover': { color: 'red' },
                                                                height: '40px',
                                                                alignSelf: 'flex-start'
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </div>
                                                ))}
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAddCountPricing}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        color: '#1D4ED8',
                                                        borderColor: '#1D4ED8',

                                                        '&:hover': {
                                                            backgroundColor: '#1D4ED8',
                                                            color: '#FFFFFF',
                                                            borderColor: '#1D4ED8',
                                                        },
                                                    }}
                                                >
                                                    Add Price Plan
                                                </Button>
                                            </div>
                                        ) : (
                                            <TextField
                                                value={Price}
                                                size="small"
                                                required
                                                id="Price"
                                                error={modalError.Price}
                                                helperText={modalError.Price ? "Price must be minimum ₹6" : ""}
                                                label="Price"
                                                onChange={handleChangePrice}
                                                variant="outlined"
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                }}
                                            />
                                        )}
                                        <TextField
                                            value={advance}
                                            size="small"
                                            id="Advance"
                                            label="Advance / Registration Fee"
                                            onChange={handleChangeAdvance}
                                            variant="outlined"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                            }}
                                        />
                                        <TextField
                                            size={"small"}
                                            id="Bank Account"
                                            select
                                            label="Bank account"
                                            defaultValue={0}
                                            value={paymentProfileID}
                                            onChange={handleChangePaymentProfile}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px',

                                                        // Default border color
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                },
                                                fontWeight: 400,

                                            }}
                                        >
                                            {bankAccountOptions?.length > 0 ?
                                                bankAccountOptions.map((option, index) => (
                                                    <MenuItem key={index} value={index}>
                                                        {option}
                                                    </MenuItem>
                                                )) :
                                                <MenuItem key="0" value={0}>
                                                    No account Found
                                                </MenuItem>
                                            }
                                        </TextField>
                                        <TextField
                                            value={groupLink}
                                            size="small"
                                            id="GroupLink"
                                            label="Group Invite Link (Whatsapp)"
                                            onChange={handleGroupLinkChange}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                fontSize: "1px",
                                            }}
                                        />

                                        {/* Add location picker after the existing fields */}
                                        <div className="flex flex-col gap-1">
                                            <p className="text-sm text-gray-600 font-medium">Location</p>
                                            <GooglePlacesAutocomplete
                                                apiKey={GEO_LOC_API || process.env.GEO_LOC_API}
                                                selectProps={{
                                                    placeholder: "Search location",
                                                    value: location,
                                                    onChange: setLocation,
                                                    components: {
                                                        DropdownIndicator: () => (
                                                            <div className="px-2">
                                                                <LocationOnIcon fontSize="small" style={{ color: '#6B7280' }} />
                                                            </div>
                                                        )
                                                    },
                                                    styles: {
                                                        input: (provided) => ({
                                                            ...provided,
                                                            backgroundColor: 'transparent',
                                                        }),
                                                        option: (provided) => ({
                                                            ...provided,
                                                            backgroundColor: 'transparent',
                                                        }),
                                                        singleValue: (provided) => ({
                                                            ...provided,
                                                            backgroundColor: 'transparent',
                                                        }),
                                                        control: (provided) => ({
                                                            ...provided,
                                                            backgroundColor: 'transparent',
                                                            borderColor: "gray",
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                                borderColor: 'black',
                                                            },
                                                            '&:focus': {
                                                                borderColor: 'black',
                                                            },
                                                            '&:active': {
                                                                borderColor: "black"
                                                            },
                                                            borderRadius: '10px',
                                                            fontSize: '14px'
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            backgroundColor: 'rgba(255, 255, 255, 1)',
                                                            zIndex: 100,
                                                        }),
                                                        menuPortal: (provided) => ({
                                                            ...provided,
                                                            zIndex: 100,
                                                        }),
                                                    },
                                                }}
                                            />
                                            {location && (
                                                <p className="text-sm text-gray-500 mt-1">
                                                    Selected: {location.label}
                                                </p>
                                            )}
                                        </div>

                                        {/* Save as field - only show when location is selected */}
                                        {location && (
                                            <TextField
                                                value={placeName}
                                                size="small"
                                                id="PlaceName"
                                                label="Save Location as"
                                                onChange={handlePlaceNameChange}
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                    fontSize: "1px",
                                                }}
                                            />
                                        )}

                                    </div>


                                    <div className={"mt-2 text-lg"}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={showMeInList}
                                                    onChange={handleChangeShowMeInList}
                                                    inputProps={{ 'aria-label': 'controlled' }}
                                                />
                                            }
                                            label={showMeInList ? "Active" : "Inactive"}
                                        />
                                    </div>
                                </Box>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={NoGSTBatch}
                                    onChange={(e) => setNoGSTBatch(e.target.checked)}
                                    sx={{
                                        '&.Mui-checked': {
                                            color: '#1D4ED8',
                                        },
                                    }}
                                />
                            }
                            label={
                                <div className="flex items-center gap-1">
                                    <span >Exclude batch from GST</span>
                                    <Tooltip
                                        title="This change will be valid only if GST is already configured"
                                        arrow
                                        placement="top"
                                    >
                                        <HelpOutlinedIcon
                                            fontSize="small"
                                            className="text-gray-500 cursor-help"
                                            sx={{ fontSize: '18px' }}
                                        />
                                    </Tooltip>
                                </div>
                            }
                        />
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={markupAllowed}
                                    onChange={(e) => setMarkupAllowed(e.target.checked)}
                                    sx={{
                                        '&.Mui-checked': {
                                            color: '#1D4ED8',
                                        },
                                    }}
                                />
                            }
                            label={
                                <div className="flex items-center gap-1">
                                    <span>Allow Markup Classes</span>
                                    <Tooltip
                                        title="Allow students to book makeup classes for missed sessions"
                                        arrow
                                        placement="top"
                                    >
                                        <HelpOutlinedIcon
                                            fontSize="small"
                                            className="text-gray-500 cursor-help"
                                            sx={{ fontSize: '18px' }}
                                        />
                                    </Tooltip>
                                </div>
                            }
                        />
                    </div>

                    <div className="mt-4">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showWorkingDays}
                                    onChange={(e) => setShowWorkingDays(e.target.checked)}
                                    sx={{
                                        '&.Mui-checked': {
                                            color: '#1D4ED8',
                                        },
                                    }}
                                />
                            }
                            label={<span>Specify Working Days</span>}
                        />

                        {showWorkingDays && (
                            <div className="mt-3">
                                <FormLabel component="legend" sx={{ mb: 2 }}>
                                    Select the days on which this service will be available:
                                </FormLabel>
                                <div className="flex flex-wrap gap-2">
                                    {daysOfWeek.map((day, index) => (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => handleDaySelection(index)}
                                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                                                ${workingDays[index] === 1
                                                    ? 'bg-cc_brand text-white'
                                                    : 'bg-[#E8EDFB] text-cc_text-brand-tertiary hover:bg-gray-200'
                                                }`}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={"flex gap-2 pt-4"}>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleClose();
                            }}
                            disabled={isLoading}
                            className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-100"
                                }`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onSubmit}
                            disabled={isLoading}
                            className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed opacity-80" : "cursor-pointer hover:bg-blue-700"
                                }`}
                        >
                            {isLoading ? (
                                <>
                                    <CircularProgress size={20} sx={{ color: 'white' }} />
                                    <span className="ml-2">Updating...</span>
                                </>
                            ) : (
                                "Save"
                            )}
                        </button>
                    </div>

                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}